/**
 * Cart items: quantity steps, typed quantities and removal in the drawer and on the /cart page — POST /cart/change.js by line item key with their sections bundled in the response, then dispatch CartUpdateEvent carrying that HTML.
 * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
 */

import { Component } from "@theme/component";
import { CartUpdateEvent, CartErrorEvent } from "@theme/events";
import { cartSectionIds } from "@theme/utilities";

/** Delay grouping rapid +/− clicks on a line into a single request. */
const STEP_DELAY = 300;

// Module-level: each response re-renders the drawer and replaces this component, requests must stay in order across instances.
let queue = Promise.resolve();
let pending = 0;

class CartItemsComponent extends Component {
  /** @type {Map<string, ReturnType<typeof setTimeout>>} */
  #timers = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this.#handleClick);
    this.addEventListener("change", this.#handleChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.#handleClick);
    this.removeEventListener("change", this.#handleChange);
  }

  /** +/− schedule a grouped update, remove commits at once; the input shows the target quantity right away. */
  #handleClick = (event) => {
    const button = event.target.closest("[data-qty-step], [data-remove]");
    const item = button?.closest("[data-key]");
    if (!item || !this.contains(button)) return;

    if (button.hasAttribute("data-remove")) {
      this.#commit(item.dataset.key, 0);
      return;
    }

    const input = item.querySelector("[data-qty-input]");
    const quantity = Math.max(1, (parseInt(input.value, 10) || 1) + Number(button.dataset.qtyStep));
    input.value = String(quantity);
    syncStepButtons(item);
    this.#schedule(item.dataset.key, quantity);
  };

  #handleChange = (event) => {
    const input = event.target.closest("[data-qty-input]");
    const item = input?.closest("[data-key]");
    if (!item) return;

    const quantity = Math.max(1, parseInt(input.value, 10) || 1);
    input.value = String(quantity);
    syncStepButtons(item);
    this.#commit(item.dataset.key, quantity);
  };

  #schedule(key, quantity) {
    clearTimeout(this.#timers.get(key));
    this.#timers.set(
      key,
      setTimeout(() => this.#commit(key, quantity), STEP_DELAY),
    );
  }

  #commit(key, quantity) {
    clearTimeout(this.#timers.get(key));
    this.#timers.delete(key);

    pending++;
    syncLoading();
    queue = queue.then(() => changeLine(key, quantity));
  }
}

/**
 * @param {string} key - clé de ligne (`item.key`)
 * @param {number} quantity - quantité cible (0 = retirer)
 */
async function changeLine(key, quantity) {
  showError(key, "");

  try {
    const sections = cartSectionIds();
    const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: key,
        quantity,
        ...(sections.length && { sections: sections.join(","), sections_url: window.location.pathname }),
      }),
    });

    const cart = await response.json().catch(() => ({}));
    if (!response.ok || cart.errors) {
      const message = typeof cart.errors === "string" ? cart.errors : cart.description || cart.message;
      throw new Error(message || `HTTP ${response.status}`);
    }

    // Drawer and header bubble listen; given the sections, the drawer re-renders synchronously, before the lines below run.
    document.dispatchEvent(
      new CartUpdateEvent(cart, "cart-items", {
        source: "cart-items",
        itemCount: cart.item_count,
        sections: cart.sections,
      }),
    );
  } catch (error) {
    console.error("[cart-items] update failed:", error);
    const fallback = messageFor(key);
    await resync().catch(() => resetQuantity(key));
    showError(key, error.message || fallback);
    document.dispatchEvent(new CartErrorEvent("cart-items", error.message));
  } finally {
    pending--;
    syncLoading();
  }
}

/** A failed change may still have moved the cart (a 422 on stock caps the line to what is left): re-render the drawer from the server. */
async function resync() {
  const ids = cartSectionIds();
  if (!ids.length) throw new Error("no cart section");

  const url = new URL(window.location.href);
  url.searchParams.set("sections", ids.join(","));
  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const sections = await response.json();
  document.dispatchEvent(new CartUpdateEvent({}, "cart-items", { source: "cart-items", sections }));
}

/** Lines matching a key in every rendered cart list (the drawer re-renders between steps, so never keep a reference). */
function linesFor(key) {
  return document.querySelectorAll(`cart-items-component [data-key="${CSS.escape(key)}"]`);
}

function showError(key, message) {
  linesFor(key).forEach((item) => {
    const error = item.querySelector("[data-cart-item-error]");
    if (error) error.textContent = message;
  });
}

/** Back to the quantity last rendered by the server. */
function resetQuantity(key) {
  linesFor(key).forEach((item) => {
    const input = item.querySelector("[data-qty-input]");
    if (input) input.value = input.defaultValue;
    syncStepButtons(item);
  });
}

/** Translated fallback message, rendered by Liquid as a data attribute of <cart-items-component>. */
function messageFor(key) {
  return linesFor(key)[0]?.closest("cart-items-component")?.dataset.errorMessage || "";
}

function syncStepButtons(item) {
  const input = item.querySelector("[data-qty-input]");
  const decrease = item.querySelector('[data-qty-step="-1"]');
  if (input && decrease) decrease.disabled = (parseInt(input.value, 10) || 1) <= 1;
}

/** Locks the drawer and the /cart page while requests are queued; re-applied after each render, whose fresh markup has no loading state. */
function syncLoading() {
  document.querySelectorAll("[data-cart-drawer-inner], [data-cart-page-inner]").forEach((el) => {
    el.dataset.loading = pending > 0 ? "true" : "false";
  });
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
